import { motion } from "framer-motion";
import { Flame, Stars, Music, Tent } from "lucide-react";
import { CAMPFIRE_IMG_1, CAMPFIRE_IMG_2 } from "./data";
import SectionHeader from "./SectionHeader";

export default function Campfire() {
  return (
    <section
      id="fire"
      data-testid="campfire-section"
      className="relative bg-[color:var(--paper)] text-[color:var(--ink)]"
    >
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-16 sm:py-20 lg:py-24">
        <SectionHeader
          eyebrow="Section 03 · Campfire & Evening"
          title={
            <>
              Nights in the Ghats <span className="font-display-italic text-[color:var(--clay)]">belong</span> to the fire.
            </>
          }
          intro="As the coffee plants disappear into the mist and the crickets begin, we lay the wood, light the pit, and gather."
        />

        <div className="mt-12 sm:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* LEFT — text on light bg */}
          <div className="lg:col-span-6 flex flex-col gap-8">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9 }}
              className="font-display text-[1.3rem] sm:text-[1.5rem] leading-[1.35] text-[color:var(--ink)]"
            >
              Bring your stories. We bring the coffee, the music, the sky —
              and a fire that lasts as long as the conversation does.
            </motion.p>

            <p className="text-[color:var(--ink)]/75 max-w-md">
              Every clear evening we set the wood in the clearing beside the
              coffee, and lay out chairs for anyone who wants to sit. It's the
              quietest, brightest, warmest end to any day at Sakleshpura.
            </p>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { icon: Flame, title: "The fire pit", body: "Lit for our guests every clear evening, safely set in the open beside the estate." },
                { icon: Stars, title: "Sakleshpura sky", body: "Away from city light — the stars look poured on, not switched on." },
                { icon: Music, title: "Songs & stories", body: "Bring an instrument, or borrow ours. The night is long, the coffee endless." },
                { icon: Tent, title: "Bring your tent", body: "Guests are welcome to pitch their own tent on the stay and camp under the sky." },
              ].map((c, i) => (
                <motion.li
                  key={c.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.7 }}
                  className="flex flex-col gap-2 border-t hairline pt-5"
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--forest)] text-[color:var(--paper)]">
                    <c.icon size={16} />
                  </span>
                  <div className="font-display text-[1.1rem] leading-tight">{c.title}</div>
                  <p className="text-[0.87rem] text-[color:var(--ink)]/70 leading-relaxed">
                    {c.body}
                  </p>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* RIGHT — two images stacked vertically */}
          <div className="lg:col-span-6 grid grid-cols-1 gap-5 sm:gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: [0.2, 0.9, 0.2, 1] }}
              className="relative aspect-[4/3] w-full overflow-hidden rounded-[24px] bg-[color:var(--ink)] ring-1 ring-[color:var(--line)]"
            >
              <img
                src={CAMPFIRE_IMG_1}
                alt="Homestay campfire evening with tent"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: 0.1, duration: 0.9, ease: [0.2, 0.9, 0.2, 1] }}
              className="relative aspect-[3/4] w-full overflow-hidden rounded-[24px] bg-[color:var(--ink)] ring-1 ring-[color:var(--line)]"
            >
              <img
                src={CAMPFIRE_IMG_2}
                alt="Bonfire close-up at Hashtag Sakleshpura Homestay"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
